import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '~/components/ui/button';
import { Input } from '~/components/ui/input';
import { Card } from '~/components/ui/card';
import { Badge } from '~/components/ui/badge';
import { Avatar, AvatarFallback } from '~/components/ui/avatar';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '~/components/ui/alert-dialog';
import { ImageWithFallback } from '~/components/figma/ImageWithFallback';
import { Plus, MessageSquare, FileText, Settings, User, CreditCard, LogOut, Trash2, Loader2 } from 'lucide-react';
import { Project, User as UserType } from '../types/api';
import { useCreateProject, useDeleteProject } from '../hooks/useProjects';
import { useProjectFiles } from '../hooks/useFiles';
import { useAuth } from '../contexts/AuthContext';

interface EnhancedProjectSidebarProps {
  projects: Project[];
  activeProjectId: string | null;
  onProjectSelect: (projectId: string) => void;
  user: UserType | null;
}

export function EnhancedProjectSidebar({ 
  projects, 
  activeProjectId, 
  onProjectSelect,
  user
}: EnhancedProjectSidebarProps) {
  const [showNewProject, setShowNewProject] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const navigate = useNavigate();
  const { logout } = useAuth();


  const createProjectMutation = useCreateProject();
  const deleteProjectMutation = useDeleteProject();

  const handleCreateProject = async () => {
    if (!newProjectName.trim()) return;

    console.log('🔍 [EnhancedProjectSidebar] handleCreateProject - Creating project:', newProjectName.trim());

    try {
      const response = await createProjectMutation.mutateAsync({
        name: newProjectName.trim(),
      });

      console.log('🔍 [EnhancedProjectSidebar] handleCreateProject - Response:', response);

      if (response.success && response.data) {
        console.log('🔍 [EnhancedProjectSidebar] handleCreateProject - Success, selecting project:', response.data.id);
        onProjectSelect(response.data.id);
        setNewProjectName('');
        setShowNewProject(false);
      }
    } catch (error: any) {
      console.error('🔍 [EnhancedProjectSidebar] Failed to create project:', error);
      // You could add a toast notification here
    }
  };

  const handleDeleteProject = async (projectId: string) => {
    console.log('🔍 [EnhancedProjectSidebar] handleDeleteProject - Deleting project:', projectId);
    
    try {
      await deleteProjectMutation.mutateAsync(projectId);
      console.log('🔍 [EnhancedProjectSidebar] handleDeleteProject - Delete successful');
      
      // If the deleted project was active, select another one
      if (activeProjectId === projectId) {
        const remainingProjects = Array.isArray(projects) ? projects.filter(p => p.id !== projectId) : [];
        console.log('🔍 [EnhancedProjectSidebar] handleDeleteProject - Remaining projects:', remainingProjects);
        
        if (remainingProjects.length > 0) {
          console.log('🔍 [EnhancedProjectSidebar] handleDeleteProject - Selecting new project:', remainingProjects[0].id);
          onProjectSelect(remainingProjects[0].id);
        } else {
          console.log('🔍 [EnhancedProjectSidebar] handleDeleteProject - No projects left, clearing selection');
          onProjectSelect('');
        }
      }
    } catch (error: any) {
      console.error('🔍 [EnhancedProjectSidebar] Failed to delete project:', error);
      // You could add a toast notification here
    }
  };

  return (
    <div className="w-80 bg-sidebar border-r border-sidebar-border flex flex-col h-full">
      {/* Logo Section */}
      <div className="p-4 border-b border-sidebar-border">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg overflow-hidden bg-sidebar-accent flex-shrink-0">
            <ImageWithFallback
              src="https://images.unsplash.com/photo-1746046936818-8d432ebd3d0e?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjB0ZWNoJTIwY29tcGFueSUyMGxvZ298ZW58MXx8fHwxNzU1MzU3ODQyfDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral"
              alt="Company Logo"
              className="w-full h-full object-cover"
            />
          </div>
          <div>
            <h1 className="font-medium text-sidebar-foreground">Bank Statement Parser</h1>
            <p className="text-xs text-sidebar-foreground/60">Project Hub</p>
          </div>
        </div>
      </div>

      {/* Credits Section */}
      <div className="p-4 border-b border-sidebar-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CreditCard className="h-4 w-4 text-sidebar-foreground/60" />
            <span className="text-sm text-sidebar-foreground/80">Credits</span>
          </div>
          <Badge variant="secondary" className="text-xs">
            {user?.creditsBalance || 0}
          </Badge>
        </div>
        <Button
          onClick={() => navigate('/dashboard/credits')}
          variant="outline"
          size="sm"
          className="w-full mt-2 text-xs"
        >
          Manage Credits
        </Button>
      </div>

      {/* Projects Section */}
      <div className="p-4 flex-1 flex flex-col">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-medium text-sidebar-foreground">Projects</h2>
          <Button
            onClick={() => setShowNewProject(true)}
            size="sm"
            variant="outline"
            className="h-8 w-8 p-0"
            disabled={createProjectMutation.isPending}
          >
            {createProjectMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Plus className="h-4 w-4" />
            )}
          </Button>
        </div>

        {createProjectMutation.isError && (
          <div className="mb-4 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-600">
            {createProjectMutation.error?.message || 'Failed to create project'}
          </div>
        )}

        {showNewProject && (
          <Card className="p-3 mb-4 bg-sidebar-accent">
            <Input
              placeholder="Project name"
              value={newProjectName}
              onChange={(e) => setNewProjectName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreateProject();
                if (e.key === 'Escape') {
                  setShowNewProject(false);
                  setNewProjectName('');
                }
              }}
              className="mb-2"
              autoFocus
            />
            <div className="flex gap-2">
              <Button 
                onClick={handleCreateProject} 
                size="sm" 
                className="flex-1"
                disabled={!newProjectName.trim() || createProjectMutation.isPending}
              >
                {createProjectMutation.isPending ? (
                  <>
                    <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create'
                )}
              </Button>
              <Button
                onClick={() => {
                  setShowNewProject(false);
                  setNewProjectName('');
                }}
                size="sm"
                variant="outline"
                className="flex-1"
                disabled={createProjectMutation.isPending}
              >
                Cancel
              </Button>
            </div>
            <p className="text-xs text-sidebar-foreground/60 mt-2">
              Projects are free to create
            </p>
          </Card>
        )}

        <div className="flex-1 overflow-y-auto space-y-2">
          {Array.isArray(projects) && projects.map((project) => (
            <ProjectCard
              key={project.id}
              project={project}
              isActive={activeProjectId === project.id}
              onSelect={() => onProjectSelect(project.id)}
              onDelete={() => handleDeleteProject(project.id)}
              isDeleting={deleteProjectMutation.isPending && deleteProjectMutation.variables === project.id}
            />
          ))}
        </div>

        {(!Array.isArray(projects) || projects.length === 0) && !showNewProject && (
          <div className="flex-1 flex items-center justify-center text-center">
            <div className="text-sidebar-foreground/60">
              <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p className="mb-2">No projects yet</p>
              <p className="text-sm">Create your first project to get started</p>
            </div>
          </div>
        )}
      </div>

      {/* User Section */}
      <div className="border-t border-sidebar-border">
        <div className="p-4">
          <div className="flex items-center gap-3">
            <Avatar className="h-8 w-8">
              <AvatarFallback className="bg-sidebar-accent text-sidebar-foreground">
                <User className="h-4 w-4" />
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sidebar-foreground truncate">
                {user?.name || 'User'}
              </p>
              <p className="text-xs text-sidebar-foreground/60 truncate">
                {user?.email || ''}
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent"
              onClick={logout}
              title="Sign out"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

interface ProjectCardProps {
  project: Project;
  isActive: boolean;
  onSelect: () => void;
  onDelete: () => void;
  isDeleting: boolean;
}

function ProjectCard({ project, isActive, onSelect, onDelete, isDeleting }: ProjectCardProps) {
  const { data: files = [] } = useProjectFiles(project.id);

  return (
    <Card
      className={`p-3 cursor-pointer transition-colors hover:bg-sidebar-accent group ${
        isActive 
          ? 'bg-sidebar-accent border-sidebar-primary' 
          : 'bg-sidebar border-sidebar-border'
      }`}
      onClick={onSelect}
    >
      <div className="flex items-start justify-between mb-2">
        <h3 className="font-medium text-sidebar-foreground truncate flex-1">
          {project.name}
        </h3>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity text-red-500 hover:text-red-600 hover:bg-red-50"
              onClick={(e) => e.stopPropagation()}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Trash2 className="h-3 w-3" />
              )}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Project</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete "{project.name}"? This action cannot be undone and will delete all associated files.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
                className="bg-red-600 hover:bg-red-700"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
      
      {project.description && (
        <p className="text-sm text-sidebar-foreground/70 mb-3 line-clamp-2">
          {project.description}
        </p>
      )}

      <div className="flex items-center gap-3 text-xs text-sidebar-foreground/60">
        <div className="flex items-center gap-1">
          <FileText className="h-3 w-3" />
          <span>{files.length}</span>
        </div>
        {project.context && (
          <div className="flex items-center gap-1">
            <Settings className="h-3 w-3" />
            <span>Context</span>
          </div>
        )}
      </div>
    </Card>
  );
}